import { Component, Input, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import {
  LoadingController,
  ModalController,
  ToastController,
} from '@ionic/angular';
import { firstValueFrom } from 'rxjs';
import { LOCAL_STORAGE } from 'src/app/shared/constants/local-storage.constants';
import { RiderStatus } from 'src/app/shared/models/rider.model';
import { AuthService } from 'src/app/shared/services/auth.service';
import { LocalStorageService } from 'src/app/shared/services/local-storage.service';
import { OtpManagerService } from 'src/app/shared/services/otp-manager.service';
import { OtpService } from 'src/app/shared/services/otp.service';
import { RiderService } from 'src/app/shared/services/rider.service';

@Component({
  selector: 'app-otp',
  templateUrl: './otp.component.html',
  styleUrls: ['./otp.component.scss'],
})
export class OtpComponent implements OnInit {
  @Input() phone!: string;
  otpConfig = {
    length: 4,
    allowNumbersOnly: true,
    isPasswordInput: false,
  };
  otpCode: string = '';
  isResendDisabled: boolean = true;
  resendCountdown: number = 20; // Countdown timer in seconds
  resendInterval: any; // Reference to the interval

  constructor(
    public modalCtrl: ModalController,
    private otpManagerService: OtpManagerService,
    public loadingCtrl: LoadingController,
    public toastCtrl: ToastController,
    private riderService: RiderService,
    private localStorage: LocalStorageService,
    private authService: AuthService,
    private router: Router
  ) {}

  ngOnInit() {
    this.startResendTimer();
  }

  onOtpChange(otp: string) {
    this.otpCode = otp;
  }

  startResendTimer() {
    this.isResendDisabled = true;
    this.resendCountdown = 20; // Reset timer to 20 seconds
    clearInterval(this.resendInterval); // Clear any existing intervals

    this.resendInterval = setInterval(() => {
      this.resendCountdown--;
      if (this.resendCountdown <= 0) {
        clearInterval(this.resendInterval);
        this.isResendDisabled = false; // Enable the button when timer finishes
      }
    }, 1000); // Update every second
  }

  async resendOtp() {
    if (this.isResendDisabled) return;
    try {
      const channel = '11'; // Use '11' for SMS, '4' for Voice, etc.
      const result = this.otpManagerService.resendOtp(channel);
      console.log('OTP resent successfully:', result);
      // Restart the timer after resending OTP
      this.startResendTimer();
    } catch (error) {
      console.error('Error while resending OTP:', error);
    }
  }

  async verifyOtp() {
    if (!this.otpCode || this.otpCode.length !== 4) {
      this.showToast('Invalid OTP entered', 'danger');
      return;
    }

    const loader = await this.loadingCtrl.create({
      message: 'Verifying OTP...',
      spinner: 'circles',
    });

    await loader.present();

    try {
      const result = await this.otpManagerService.verifyOtp(this.otpCode);
      if (result.type !== 'success') {
        throw new Error('OTP verification failed');
      }

      const user = await this.authService.loginAnonymously();
      console.log('Anonymous user logged in:', user);

      let riderData = await firstValueFrom(
        this.riderService.getRiderByContact(this.phone)
      );

      if (!riderData) {
        console.warn('No rider found. Checking backend...');
        const backendRider = await firstValueFrom(
          this.riderService.getRiderByPrimaryContact(this.phone)
        );

        if (!backendRider) {
          console.warn('User not found in backend either.');
          await this.modalCtrl.dismiss();
          this.router.navigate(['/welcome'])
          throw new Error('User not found! Please register to continue...');
        }

        await this.riderService.createRiderInFirebase(backendRider);
        riderData = await firstValueFrom(
          this.riderService.getRiderByContact(this.phone)
        );
      }

      if (riderData) {
        this.localStorage.setItem(LOCAL_STORAGE.RIDER, riderData);
        this.riderService.updateRiderStatus(
          riderData.riderId,
          RiderStatus.AVAILABLE
        );
        await this.router.navigate(['/rider-dashboard']);
      }

      await this.modalCtrl.dismiss({ verified: true });
      this.showToast('OTP verified successfully!', 'success');
    } catch (error: any) {
      console.error('Error:', error);
      this.showToast(
        error.message || 'An error occurred. Please try again.',
        'danger'
      );
      await this.modalCtrl.dismiss();
    } finally {
      await loader.dismiss();
    }
  }

  async showToast(
    message: string,
    color: 'success' | 'danger' | 'warning' | 'primary' = 'primary'
  ) {
    const toast = await this.toastCtrl.create({
      message,
      duration: 2000,
      position: 'bottom',
      color,
    });
    toast.present();
  }

  closeModal(data?: any) {
    this.modalCtrl.dismiss(data);
  }
}
